/**
 * The Refused photos tab's wire and rules (spec docs/superpowers/specs/2026-10-13-refused-photo-review-design.md §6).
 *
 * JSX-free on purpose, so root jest runs it (tests/unit/admin/refused-photos-api.test.ts).
 * The API answers these routes with the result itself — no `{ ok, data }` envelope
 * (backend/src/index.ts → createSuccessResponse) — and refuses with RFC 9457 Problem
 * Details. A 5xx body has no `detail` and its `title` is only the reason phrase, so
 * every code the routes send has its own sentence here.
 */
import { AdminApiError, adminFetch } from '@/providers/admin-api';
import { apiErrorMessage } from '@/lib/api-error';

import { REFUSED_ERROR_TEXT, type QuarantineList, type Section } from './refused-photos-model';

export * from './refused-photos-model';

export const REFUSED_PATHS = {
  list: '/v1/admin/moderation/quarantine',
  action: (photoId: string, action: 'reveal' | 'confirm' | 'restore' | 'escalate') =>
    `/v1/admin/moderation/quarantine/${photoId}/${action}`,
} as const;

export const PAGE_LIMIT = 50;

const FORBIDDEN_TEXT = 'You do not have permission for this action.';

export class RefusedActionError extends Error {
  constructor(
    public readonly status: number | null,
    public readonly code: string | null,
    message: string,
  ) {
    super(message);
    this.name = 'RefusedActionError';
  }
}

function toRefusedError(e: unknown): RefusedActionError {
  if (e instanceof AdminApiError) {
    const body = e.body && typeof e.body === 'object' ? (e.body as { code?: unknown }) : undefined;
    const code = typeof body?.code === 'string' && body.code.length > 0 ? body.code : null;
    const known = code ? REFUSED_ERROR_TEXT[code] : undefined;
    const message = known ?? (e.status === 403 ? FORBIDDEN_TEXT : apiErrorMessage(e.body, e.status));
    return new RefusedActionError(e.status, code, message);
  }
  return new RefusedActionError(null, null, e instanceof Error && e.message ? e.message : 'The request failed.');
}

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  try {
    const res = init ? await adminFetch(url, init) : await adminFetch(url);
    return (await res.json()) as T;
  } catch (e) {
    throw toRefusedError(e);
  }
}

export function fetchRefused(apiUrl: string, status: 'pending' | 'decided' = 'pending', page = 1): Promise<QuarantineList> {
  return call<QuarantineList>(`${apiUrl}${REFUSED_PATHS.list}?status=${status}&limit=${PAGE_LIMIT}&page=${page}`);
}

/** Audited server-side BEFORE the URL is signed; the URL is good for one look (60 s). */
export function revealRefused(apiUrl: string, photoId: string, reason: string): Promise<{ url: string; expires_in: number }> {
  return call(`${apiUrl}${REFUSED_PATHS.action(photoId, 'reveal')}`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason.trim() }),
  });
}

function decide<T>(apiUrl: string, photoId: string, action: 'confirm' | 'restore' | 'escalate', notes?: string): Promise<T> {
  const note = notes?.trim();
  return call<T>(`${apiUrl}${REFUSED_PATHS.action(photoId, action)}`, {
    method: 'POST',
    body: JSON.stringify(note ? { notes: note } : {}),
  });
}

export const confirmRefused = (apiUrl: string, photoId: string, notes?: string) =>
  decide<{ decision: 'confirmed' }>(apiUrl, photoId, 'confirm', notes);
export const restoreRefused = (apiUrl: string, photoId: string, notes?: string) =>
  decide<{ decision: 'restored'; visibility: Section; is_primary: boolean }>(apiUrl, photoId, 'restore', notes);
export const escalateRefused = (apiUrl: string, photoId: string, notes?: string) =>
  decide<{ decision: 'legal_hold' }>(apiUrl, photoId, 'escalate', notes);
